import dayjs from 'dayjs'
import { FC } from 'react'
import { Matcher } from 'react-day-picker'

import { Calendar } from '@/components/shared/calendar'

import { DateRange } from '../Calendar.types'
import { CalendarRangeBadge } from '../CalendarRangeBadge'
import { CalendarCaption } from '../caption/CalendarCaption'
import { HoursCaption } from '../caption/HoursCaption'
import { EmptyChevron } from '../EmptyChevron'
import { DateCalendarSkeleton } from './DateCalendarSkeleton'

type Props = {
  isHoursType: boolean
  range?: DateRange
  month: Date
  setMonth: (date: Date) => void
  fromMonth?: Date
  resolvedDisabled: {
    before?: Date
    after?: Date
  }
  disabledMatchers: Matcher[]
  showSelectedDate: boolean
  onMonthClick: () => void
  onHoursDateChange: (date: Date) => void
  onSelectDateForHours: (selectedDate?: Date) => void
  onSelectDate: (selectedDate: Date, selectedRange?: DateRange) => void
  onRangeReset: () => void
  individualSlots?: {
    bordered: { from: string; to: string }[]
    disabled: { from: string; to: string }[]
  }
  isLoading?: boolean
  showOnlyRangeEnd?: boolean
}

export const DateCalendarPanel: FC<Props> = ({
  isHoursType,
  range,
  month,
  setMonth,
  fromMonth,
  resolvedDisabled,
  disabledMatchers,
  showSelectedDate,
  onMonthClick,
  onHoursDateChange,
  onSelectDateForHours,
  onSelectDate,
  onRangeReset,
  individualSlots,
  isLoading,
  showOnlyRangeEnd
}) => {
  if (isLoading) {
    return <DateCalendarSkeleton />
  }

  return (
    <div className="px-4">
      {isHoursType ? (
        <Calendar
          classNames={{
            head_cell: 'w-36 h-34 xxs:w-49',
            cell: 'w-36 h-34 xxs:w-49'
          }}
          className="mb-16"
          mode="single"
          selected={range?.from ? dayjs(range.from).toDate() : undefined}
          components={{
            MonthCaption: () => (
              <HoursCaption
                currentDate={month}
                month={month}
                setMonth={setMonth}
                onDateChange={onHoursDateChange}
                navigateByMonth
                onTitleClick={onMonthClick}
                pickerMode="calendar"
                disabled={resolvedDisabled}
              />
            ),
            Chevron: EmptyChevron
          }}
          onSelect={onSelectDateForHours}
          defaultMonth={month}
          onMonthChange={setMonth}
          month={month}
          disabled={disabledMatchers}
          individualSlots={individualSlots}
        />
      ) : (
        <Calendar
          classNames={{
            head_cell: 'w-36 h-34 xxs:w-49',
            cell: 'w-36 h-34 xxs:w-49'
          }}
          className="mb-16"
          mode="range"
          excludeDisabled
          selected={{
            from: range?.from ? dayjs(range.from).toDate() : undefined,
            to: range?.to ? dayjs(range.to).toDate() : undefined
          }}
          components={{
            MonthCaption: () => (
              <CalendarCaption
                onMonthClick={onMonthClick}
                month={month}
                setMonth={setMonth}
                fromMonth={fromMonth}
                disabled={resolvedDisabled}
              />
            ),
            Chevron: EmptyChevron
          }}
          onSelect={(selectedRange, selectedDate) =>
            onSelectDate(selectedDate, selectedRange)
          }
          defaultMonth={range?.from}
          onMonthChange={setMonth}
          month={month}
          disabled={disabledMatchers}
          individualSlots={individualSlots}
        />
      )}
      {showSelectedDate && range?.from ? (
        <CalendarRangeBadge
          range={range}
          onRangeReset={onRangeReset}
          showOnlyRangeEnd={showOnlyRangeEnd}
        />
      ) : (
        <div className="h-[32px]" />
      )}
      {(individualSlots?.bordered.length ?? 0) > 0 && (
        <div className="mt-8 font-bold text-grey-60 text-12">
          <span className="text-bronze-40">—</span> Мои бронирования
        </div>
      )}
    </div>
  )
}
